import { Component } from '@theme/component';
import { onDocumentLoaded } from '@theme/utilities';
import { gsap } from '@theme/gsap';
import { reloadScripts, hydrate } from '@theme/section-hydration';

export class PageTransition extends Component {
  connectedCallback() {
    onDocumentLoaded(() => this.init());
  }

  init() {
    this.overlay = this.querySelector('.overlay__path');
    this.titleEl = this.querySelector('.page-transition-title');
    if (!this.overlay || !this.titleEl) return;

    // Default site title from data attribute (e.g., {{ shop.name }})
    this.defaultTitle = this.titleEl.dataset.title?.trim() || 'Hyrik Studio';

    this.style.visibility = 'hidden';
    this.bindLinks();
  }

  bindLinks() {
    document.addEventListener(
      'click',
      (e) => {
        const link = e.target.closest('a[href]');
        if (!link) return;

        const href = link.getAttribute('href');
        if (!href || href.startsWith('#') || link.target === '_blank') return;

        const url = new URL(href, window.location.origin);
        const isInternal = url.origin === window.location.origin;

        if (isInternal) {
          e.preventDefault();
          this.pageExitAnimation(url.href);
        }
      },
      true
    );

    window.addEventListener('popstate', () => location.reload());
  }

  async fetchPage(url) {
    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return {
        newHeader: doc.querySelector('#header-group'),
        newMain: doc.querySelector('main'),
        newTitleFull: doc.querySelector('title')?.innerText || '',
      };
    } catch (err) {
      console.error('Page fetch error:', err);
      return null;
    }
  }

  splitText(text) {
    this.titleEl.innerHTML = '';
    text.split('').forEach((char) => {
      const span = document.createElement('span');
      span.textContent = char === ' ' ? '\u00A0' : char;
      this.titleEl.appendChild(span);
    });
    return this.titleEl.querySelectorAll('span');
  }

  get paths() {
    return {
      step1: {
        unfilled: 'M 0 100 V 100 Q 50 100 100 100 V 100 z',
        inBetween: {
          curve1: 'M 0 100 V 50 Q 50 0 100 50 V 100 z',
          curve2: 'M 0 100 V 50 Q 50 100 100 50 V 100 z',
        },
        filled: 'M 0 100 V 0 Q 50 0 100 0 V 100 z',
      },
      step2: {
        filled: 'M 0 0 V 100 Q 50 100 100 100 V 0 z',
        inBetween: {
          curve1: 'M 0 0 V 50 Q 50 0 100 50 V 0 z',
          curve2: 'M 0 0 V 50 Q 50 100 100 50 V 0 z',
        },
        unfilled: 'M 0 0 V 0 Q 50 0 100 0 V 0 z',
      },
    };
  }

  async pageExitAnimation(url) {
    window.dispatchEvent(new Event('page-transition:start'));
    this.style.visibility = 'visible';

    const overlay = this.overlay;
    const paths = this.paths;
    const fetchPromise = this.fetchPage(url);

    // Hide text before overlay animation to avoid flicker
    this.titleEl.style.opacity = '0';

    // Overlay morph up
    const overlayTl = gsap.timeline();
    overlayTl
      .set(overlay, { attr: { d: paths.step1.unfilled } })
      .to(overlay, {
        duration: 0.8,
        ease: 'power4.in',
        attr: { d: paths.step1.inBetween.curve1 },
      })
      .to(overlay, {
        duration: 0.4,
        ease: 'power2.out',
        attr: { d: paths.step1.filled },
      });

    await overlayTl;

    const result = await fetchPromise;
    if (!result || !result.newMain) return (window.location.href = url);

    const { newHeader, newMain, newTitleFull } = result;
    const currentMain = document.querySelector('main');
    const currentHeader = document.querySelector('#header-group');

    // Extract title and fallback if too long (>3 words)
    let cleanTitle = (newTitleFull || '').split(/\s[-–]\s/)[0].trim();
    const wordCount = cleanTitle.split(/\s+/).filter(Boolean).length;
    if (wordCount > 3) cleanTitle = this.defaultTitle;

    const letters = this.splitText(cleanTitle);

    // Show title element again
    this.titleEl.style.opacity = '1';

    // Animate text appearance
    const textTl = gsap.timeline();
    textTl.fromTo(
      letters,
      { yPercent: 80, opacity: 0 },
      {
        yPercent: 0,
        opacity: 1,
        duration: 0.55,
        ease: 'power4.out',
        stagger: 0.015,
      }
    );

    // Replace both header + main safely
    if (newHeader && currentHeader) currentHeader.replaceWith(newHeader);
    if (newMain && currentMain) currentMain.replaceWith(newMain);

    // Re-run scripts and hydrate sections
    reloadScripts(newHeader);
    reloadScripts(newMain);

    // Scroll & update URL
    window.scrollTo({ top: 0, behavior: 'instant' });
    history.pushState({}, '', url);
    if (newTitleFull) document.title = newTitleFull;

    // Fire next animation
    textTl.eventCallback('onComplete', () => this.pageEnterAnimation(letters));
  }

  pageEnterAnimation(letters) {
    const overlay = this.overlay;
    const paths = this.paths;

    const tl = gsap.timeline({
      onComplete: () => {
        this.style.visibility = 'hidden';
        this.titleEl.textContent = '';
        window.dispatchEvent(new Event('page-transition:done'));
      },
    });

    tl.to(letters, {
      yPercent: -80,
      opacity: 0,
      duration: 0.45,
      ease: 'power4.in',
      stagger: 0.01,
    });

    tl.set(overlay, { attr: { d: paths.step2.filled } })
      .to(overlay, {
        duration: 0.4,
        ease: 'power4.in',
        attr: { d: paths.step2.inBetween.curve1 },
      })
      .to(overlay, {
        duration: 0.5,
        ease: 'power4.out',
        attr: { d: paths.step2.unfilled },
      })
      .to(
        overlay,
        {
          duration: 0.6,
          ease: 'power4.out',
          attr: { d: paths.step2.unfilled },
        },
        '-=0.2'
      );
  }
}

customElements.define('page-transition', PageTransition);